import Text from "@/comps/text"

import { appStoreFallback } from "@/utils/branch"
import {
	Desktop,
	devices,
	Mobile,
	Tablet,
} from "@/utils/breakpoints"
import { getAnalytics, logEvent } from "firebase/analytics"

import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { Box, FlexBox } from "react-styled-flex"
import styled, { css } from "styled-components"

import useAuth from "@/utils/client-auth"
import clsx from "clsx"
import { useState } from "react"

const LinkColumns = styled.div`
	display: flex;
	flex-direction: row;
	flex: 2;

	margin-bottom: 20px;

	flex-wrap: wrap;
	row-gap: 10px;

	@media ${devices.tablet} {
		flex-wrap: nowrap;
		justify-content: space-between;
		margin-bottom: 24px;
		width: 90%;
	}

	@media ${devices.desktop} {
		margin-bottom: 33px;
		width: 1170px;
	}
`

const aiItems: FooterSectionItem[] = [
	{
		label: "txt_ai_ads_generator",
		url: "/tools/ai-ad-generator",
	},
	{
		label: "txt_product_url_to_ads",
		url: "/tools/url-to-video-maker",
	},
	{
		label: "txt_script_generator",
		url: "/tools/ai-script-generator",
	},
	{
		label: "txt_script_to_ads_generator",
		url: "/tools/ai-script-to-video-generator",
	},
]

const productItems: FooterSectionItem[] = [
	{
		label: "Vidsup App",
		raw: true,
		newTab: true,
		url: appStoreFallback,
	},
	{ label: "lbl_pricing", url: "/pricing" },
]

const companyItems: FooterSectionItem[] = [
	{ label: "lbl_contact_us", url: "/contact" },
	{
		label: "txt_terms_and_privacy",
		url: "/privacy-policy",
	},
]

const Links = () => {
	const router = useRouter()
	const { t } = useTranslation()
	const [section, setSection] = useState<string | null>(
		null,
	)
	const { userInfo } = useAuth()

	const setOpen = (item: string) => {
		if (section === item) {
			setSection(null)
		} else {
			setSection(item)
		}
	}
	return (
		<>
			<Desktop>
				<LinkColumns>
					<FooterSection items={aiItems}>
						lbl_ai_tools
					</FooterSection>
					<FooterSection items={productItems}>
						lbl_product
					</FooterSection>
					<FooterSection items={companyItems}>
						lbl_company
					</FooterSection>
				</LinkColumns>
			</Desktop>

			<Tablet className="flex w-full items-center justify-center">
				<LinkColumns>
					<FooterSection items={aiItems}>
						lbl_ai_studio
					</FooterSection>
					<FooterSection items={productItems}>
						lbl_product
					</FooterSection>
					<FlexBox column gap={12}>
						<FooterSection items={companyItems}>
							lbl_company
						</FooterSection>
					</FlexBox>
				</LinkColumns>
			</Tablet>

			<Mobile className="mb-2 mt-[10px] w-full">
				<div className="flex flex-col">
					<button
						onClick={() => setOpen("ai_tools")}
						className="flex w-full flex-row items-center justify-between py-[14px] text-[16px] font-500 text-blue-600">
						{t("lbl_ai_tools")}

						<div className="button relative max-h-[16px] min-h-[16px] min-w-[16px] max-w-[16px] cursor-pointer">
							<span
								className={clsx(
									section === "ai_tools" && "rotate-90",
									"before-line absolute bg-blue-600 transition-transform duration-[250ms] ease-out",
								)}></span>
							<span
								className={clsx(
									"bg-blue-600",
									"after-line absolute transition-transform duration-[250ms] ease-out",
								)}></span>
						</div>
					</button>

					{section === "ai_tools" && (
						<div className="flex w-full flex-col gap-5 pb-5">
							{aiItems.map((item, key) => {
								if (item.url !== undefined) {
									return (
										<a
											key={key}
											href={item.url}
											className="flex-1 shrink-0 text-[16px] font-400 text-blue-800"
											onClick={() =>
												logEvent(
													getAnalytics(),
													"web_footer",
													{
														page:
															router.route.slice(1) ||
															"home",
														link: item.url,
														uid: userInfo?.userId,
													},
												)
											}
											target={
												item.newTab ? "_blank" : undefined
											}
											rel="noopener noreferrer">
											{item.raw
												? item.label
												: t(item.label)}
										</a>
									)
								}

								return (
									<button
										key={key}
										className="flex-1 shrink-0 text-[16px] font-400 text-blue-800"
										onClick={() => {
											item.action && item.action()
											logEvent(
												getAnalytics(),
												"web_footer",
												{
													page:
														router.route.slice(1) || "home",
													link: item.url,
													uid: userInfo?.userId,
												},
											)
										}}>
										{item.raw ? item.label : t(item.label)}
									</button>
								)
							})}
						</div>
					)}
				</div>

				<div className="h-px w-full shrink-0 bg-blue-300"></div>

				<div className="flex flex-col">
					<button
						onClick={() => setOpen("product")}
						className="flex w-full flex-row items-center justify-between py-[14px] text-[16px] font-500 text-blue-600">
						{t("lbl_product")}

						<div className="button relative max-h-[16px] min-h-[16px] min-w-[16px] max-w-[16px] cursor-pointer">
							<span
								className={clsx(
									section === "product" && "rotate-90",
									"before-line absolute bg-blue-600 transition-transform duration-[250ms] ease-out",
								)}></span>
							<span
								className={clsx(
									"bg-blue-600",
									"after-line absolute transition-transform duration-[250ms] ease-out",
								)}></span>
						</div>
					</button>

					{section === "product" && (
						<div className="flex w-full flex-col gap-5 pb-5">
							{productItems.map((item, key) => {
								if (item.url !== undefined) {
									return (
										<a
											key={key}
											href={item.url}
											className="flex-1 shrink-0 text-[16px] font-400 text-blue-800"
											onClick={() =>
												logEvent(
													getAnalytics(),
													"web_footer",
													{
														page:
															router.route.slice(1) ||
															"home",
														link: item.url,
														uid: userInfo?.userId,
													},
												)
											}
											target={
												item.newTab ? "_blank" : undefined
											}
											rel="noopener noreferrer">
											{item.raw
												? item.label
												: t(item.label)}
										</a>
									)
								}

								return (
									<button
										key={key}
										className="flex-1 shrink-0 text-[16px] font-400 text-blue-800"
										onClick={() => {
											item.action && item.action()
											logEvent(
												getAnalytics(),
												"web_footer",
												{
													page:
														router.route.slice(1) || "home",
													link: item.url,
													uid: userInfo?.userId,
												},
											)
										}}>
										{item.raw ? item.label : t(item.label)}
									</button>
								)
							})}
						</div>
					)}
				</div>

				<div className="h-px w-full shrink-0 bg-blue-300"></div>

				<div className="flex flex-col">
					<button
						onClick={() => setOpen("company")}
						className="flex w-full flex-row items-center justify-between py-[14px] text-[16px] font-500 text-blue-600">
						{t("lbl_company")}

						<div className="button relative max-h-[16px] min-h-[16px] min-w-[16px] max-w-[16px] cursor-pointer">
							<span
								className={clsx(
									section === "company" && "rotate-90",
									"before-line absolute bg-blue-600 transition-transform duration-[250ms] ease-out",
								)}></span>
							<span
								className={clsx(
									"bg-blue-600",
									"after-line absolute transition-transform duration-[250ms] ease-out",
								)}></span>
						</div>
					</button>

					{section === "company" && (
						<div className="flex w-full flex-col gap-5 pb-5">
							{companyItems.map((item, key) => {
								if (item.url !== undefined) {
									return (
										<a
											key={key}
											href={item.url}
											className="flex-1 shrink-0 text-[16px] font-400 text-blue-800"
											onClick={() =>
												logEvent(
													getAnalytics(),
													"web_footer",
													{
														page:
															router.route.slice(1) ||
															"home",
														link: item.url,
														uid: userInfo?.userId,
													},
												)
											}
											target={
												item.newTab ? "_blank" : undefined
											}
											rel="noopener noreferrer">
											{item.raw
												? item.label
												: t(item.label)}
										</a>
									)
								}

								return (
									<button
										key={key}
										className="flex-1 shrink-0 text-[16px] font-400 text-blue-800"
										onClick={() => {
											item.action && item.action()
											logEvent(
												getAnalytics(),
												"web_footer",
												{
													page:
														router.route.slice(1) || "home",
													link: item.url,
													uid: userInfo?.userId,
												},
											)
										}}>
										{item.raw ? item.label : t(item.label)}
									</button>
								)
							})}
						</div>
					)}
				</div>
			</Mobile>
		</>
	)
}

interface FooterSectionItem {
	label: string
	action?: () => void
	url?: string
	newTab?: boolean
	raw?: boolean
}

interface FooterSectionProps {
	children?: string
	items: FooterSectionItem[]
}

const SectionContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	width: 171px;

	@media screen and (min-width: 1225px) {
		flex: 1;
		padding: 0;
	}
`

const sectionText = css`
	font-size: 14px;
	font-weight: 400;
	color: var(--color-blue-800);
	text-transform: capitalize;
	text-align: left;
	flex: 1;

	cursor: pointer;

	transition: color 200ms ease-in-out;

	:hover {
		color: var(--color-blue-600);
	}
`

const SectionButton = styled.button`
	${sectionText};
`

const SectionAnchor = styled.a`
	${sectionText};
`

const FooterSection = (props: FooterSectionProps) => {
	const { t } = useTranslation()
	const { userInfo } = useAuth()

	const router = useRouter()

	return (
		<SectionContainer>
			{props.children && (
				<Box padding="33px 0px 20px 0px">
					<Text
						content={props.children}
						size="16"
						weight="500"
						color="600"
						capitalization="uppercase"
						letterSpacing="1.28"
					/>
				</Box>
			)}
			<FlexBox
				className={clsx(!props.children && "pt-[77px]")}
				column
				gap={12}>
				{props.items.map((item, key) => {
					if (item.url !== undefined)
						return (
							<SectionAnchor
								key={key}
								href={item.url}
								onClick={() =>
									logEvent(getAnalytics(), "web_footer", {
										page: router.route.slice(1) || "home",
										link: item.url,
										uid: userInfo?.userId,
									})
								}
								target={item.newTab ? "_blank" : undefined}
								rel="noopener noreferrer">
								{item.raw ? item.label : t(item.label)}
							</SectionAnchor>
						)
					return (
						<SectionButton
							key={key}
							onClick={() => {
								item.action && item.action()
								logEvent(getAnalytics(), "web_footer", {
									page: router.route.slice(1) || "home",
									link: item.url,
									uid: userInfo?.userId,
								})
							}}>
							{item.raw ? item.label : t(item.label)}
						</SectionButton>
					)
				})}
			</FlexBox>
		</SectionContainer>
	)
}

export default Links
